import React, { useState, useEffect } from 'react';
import './modalcookies.css';

declare global {
  interface Window {
    dataLayer: any[];
    google_tag_manager?: any;
  }
}

interface ModalCookiesProps {
  showModal: boolean | null;
  onClose: () => void;
}

const ModalCookies = ({ showModal, onClose }: ModalCookiesProps) => {
  const [analyticsAccepted, setAnalyticsAccepted] = useState<boolean>(false);
  const [customizeMode, setCustomizeMode] = useState<boolean>(false);
  const [tempAnalyticsAccepted, setTempAnalyticsAccepted] = useState<boolean>(false);

  useEffect(() => {
    const cookiesAccepted = localStorage.getItem('cookies-accepted') === 'true';
    const analyticsFromStorage = localStorage.getItem('analytics-accepted') === 'true';

    if (!cookiesAccepted) {
      setAnalyticsAccepted(false);
      setTempAnalyticsAccepted(false);
    } else {
      setAnalyticsAccepted(analyticsFromStorage);
      setTempAnalyticsAccepted(analyticsFromStorage);
    }
  }, [showModal]);

  useEffect(() => {
    configureAnalytics();
  }, [analyticsAccepted]);

  const configureAnalytics = () => {
    if (analyticsAccepted) {
      loadGoogleTagManager();
      addNoScriptTag();
    } else {
      removeAnalyticsScript();
    }
  };

    const removeAnalyticsScript = () => {
        document.getElementById("gtm-script")?.remove();
        document.getElementById("gtm-noscript")?.remove();

        document.querySelectorAll('script[src*="googletagmanager"]').forEach((s) => s.remove());
        document.querySelectorAll('iframe[src*="googletagmanager"]').forEach((i) => i.remove());

        window.dataLayer = [];
        delete window.google_tag_manager;

        if (window.dataLayer && window.dataLayer.length) {
            window.dataLayer.length = 0;
        }

        if (window.dataLayer) {
            window.dataLayer = [];
        }

        if (window.google_tag_manager) {
            Object.keys(window.google_tag_manager).forEach(key => delete window.google_tag_manager[key]);
            delete window.google_tag_manager;
        }
    };
  const loadGoogleTagManager = () => {
    if (document.getElementById("gtm-script")) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const script = document.createElement("script");
    script.id = "gtm-script";
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtm.js?id=GTM-TJ88RF7N";
    document.head.appendChild(script);
  };

  const addNoScriptTag = () => {
    if (document.getElementById("gtm-noscript")) return;

    const noscript = document.createElement("noscript");
    noscript.id = "gtm-noscript";
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-TJ88RF7N"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;
    document.body.prepend(noscript);
  };

  const handleCustomize = () => {
    setCustomizeMode(true);
  };

  const handleAnalyticsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempAnalyticsAccepted(e.target.checked);
  };

  const handleSave = () => {
    localStorage.setItem('analytics-accepted', tempAnalyticsAccepted ? 'true' : 'false');
    localStorage.setItem('cookies-accepted', 'true');
    setAnalyticsAccepted(tempAnalyticsAccepted);
    setCustomizeMode(false);
    onClose();
  };

  const handleAcceptAll = () => {
    localStorage.setItem('analytics-accepted', 'true');
    localStorage.setItem('cookies-accepted', 'true');
    setAnalyticsAccepted(true);
    setTempAnalyticsAccepted(true);
    setCustomizeMode(false);
    onClose();
  };

  if (!showModal) return null;

  return (
    <div className="modal-cookies">
      <div className="modal-cookies-content">
        <h2>Informacja o plikach cookies</h2>
        <p>
          Korzystamy z plików cookies, aby zapewnić prawidłowe działanie aplikacji oraz mierzyć jej wydajność za pomocą Google Analytics.
        </p>
        <p>
          Zbieramy informacje o Twojej aktywności na stronie w celu analizy i ulepszania jej funkcjonalności.
        </p>
        <p>
          Możesz wybrać, jakie dane chcesz udostępnić, dostosowując ustawienia w <strong>„Ustawieniach Cookies”</strong>.
        </p>
        <p>
          Klikając <strong>„Akceptuję”</strong>, wyrażasz zgodę na przetwarzanie przez Administratora oraz Zaufanych Partnerów danych związanych z analizą statystyk.
        </p>
        <p>
          W każdej chwili możesz wycofać zgodę lub zmienić ustawienia w <strong>„Ustawieniach Cookies”</strong>.
        </p>
        <p>
          Aby dowiedzieć się więcej, przeczytaj naszą{' '}
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
            Politykę Prywatności
          </a>.
        </p>

        {customizeMode && (
          <div className="cookies-checkboxes">
            <div className="cookie-checkbox">
              <input type="checkbox" id="required-cookies" disabled checked />
              <label htmlFor="required-cookies">Wymagane pliki cookies</label>
            </div>
            <div className="cookie-checkbox">
              <input
                type="checkbox"
                id="analytics-cookies"
                checked={tempAnalyticsAccepted}
                onChange={handleAnalyticsChange}
              />
              <label htmlFor="analytics-cookies">Pliki cookies analityczne</label>
            </div>
          </div>
        )}

        <div className="buttons-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
          {!customizeMode ? (
            <>
              <button className="button customize-button" onClick={handleCustomize}>
                Ustawienia cookies
              </button>
              <button className="button accept-button" onClick={handleAcceptAll}>
                Akceptuję
              </button>
            </>
          ) : (
            <>
              <button className="button accept-all-button" onClick={handleAcceptAll}>
                Akceptuj wszystkie
              </button>
              <button className="button accept-button" onClick={handleSave}>
                Zapisz
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalCookies;
